import * as React from 'react';
import { translate } from 'react-i18next';
import * as moment from 'moment';
import * as c from 'models/constants';
const { connect } = require('react-redux');
import { LoadingOverlayWrapper, AvatarSmall } from 'components';
import { ErrorPage } from 'containers';
import { IOrderCardAction, IShopOrderCardData, IOrderCardData } from 'models/orders/ordercard';
import { clearData, shipOrder } from 'redux/modules/shop/shopordercard';
import { formatCurrency, formatDuration, fullPhoneNumber } from 'helpers/common';
import { IOrderItem, IOrder, IShopOrder } from 'models/orders/order';
import { Appointment } from 'components/Common/Appointment';
import { IAppointment } from 'models/shop/appointments';
import renderIcon from 'helpers/renderIcon';
import { EllipseBlackWhiteButton, EllipseGreyBlackButton, WhiteButton, RedButton } from 'components/Common/Button';
import { OrderPaymentStatus, OrderStatus } from 'models';
import { setStatus } from 'redux/modules/shop/orders';
import { IOrdersAction } from 'models/orders/orders';
import { showModal, closeModal } from 'redux/modules/modal';
import { PortalOneColumnLayout } from 'components/PortalStructure';
import { cancelOrder } from 'redux/modules/user/orders';
import { browserHistory, Link } from 'react-router';
import { momentWithTimezone } from 'redux/modules/shop/helpers';
require('./style.scss');

const parseDate = (date) => moment(date).format('LL');

type ProductsAndServices = { products: IOrderItem[]; services: IOrderItem[] };

const OrderProductItem = ({
	productId,
	productPicture,
	productName,
	productDescription,
	productSKU,
	formattedPrice,
}) => (
	<div className="product-card" key={productId}>
		<div className="product-image">{productPicture && <img src={productPicture} alt={`${productName} picture`} />}</div>
		<div className="product-details">
			<h4>{productName}</h4>
			<p>{productDescription}</p>
			{productSKU && <p>{productSKU}</p>}
		</div>
		<p className="product-price">{formattedPrice}</p>
	</div>
);

interface IProps {
	id?: string;
	params?: IParams;
	orderCard: IOrderCardData | IShopOrderCardData;
	viewType: 'shop' | 'user';
	backLink: string;

	/* redux */
	clearData?: () => IOrderCardAction;
	shipOrder?: (id: string) => void;
	setStatus?: (
		shopOrderId: string,
		shopId: string,
		status: OrderStatus,
		note: string,
		isSingleOrder: boolean,
	) => IOrdersAction;
	showModal?: (modalType: string, modalProps: any) => any;
	closeModal?: () => any;
	cancelOrder?: (orderId) => any;
	t?: (key: string) => string;
}

interface IParams {
	id?: string;
	shopOrderId?: string;
}

interface IState {
	shippingModalOpen: boolean;
}

@connect(
	() => ({}),
	(dispatch) => ({
		clearData: () => dispatch(clearData()),
		shipOrder: (id: string) => dispatch(shipOrder(id)),
		setStatus: (shopOrderId: string, shopId: string, status: OrderStatus, note: string, isSingleOrder: boolean) =>
			dispatch(setStatus(shopOrderId, shopId, status, note, isSingleOrder)),
		closeModal: () => dispatch(closeModal()),
		showModal: (modalType: string, modalProps: any) => dispatch(showModal(modalType, modalProps)),
		cancelOrder: (id) => dispatch(cancelOrder(id, true)),
	}),
)
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class OrderDetails extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.state = {
			shippingModalOpen: false,
		};

		this.handleShipOrderClick = this.handleShipOrderClick.bind(this);
		this.renderShopOrderAppointments = this.renderShopOrderAppointments.bind(this);
		this.toggleShippingModal = this.toggleShippingModal.bind(this);
		this.confirmOrder = this.confirmOrder.bind(this);
		this.rejectOrder = this.rejectOrder.bind(this);
		this.calculateAppointmentTimes = this.calculateAppointmentTimes.bind(this);
	}

	private rejectOrder(shopOrderId: string, orderId: string, shopId?: string) {
		const { t, showModal, setStatus, closeModal } = this.props;

		const confirmFnc = (confirm: boolean) => {
			closeModal();
			if (confirm) {
				if (this.isShopOrder()) {
					setStatus(shopOrderId, shopId, OrderStatus.Rejected, '', true);
				} else {
					this.props.cancelOrder(orderId);
				}
			}
		};

		showModal('ConfirmationDialog', {
			customFooterContent: (
				<>
					<WhiteButton onClick={() => confirmFnc(false)}>{t('BSPortal_Common_Cancel')}</WhiteButton>
					<RedButton onClick={() => confirmFnc(true)}>{t('Orders_OrderItemCard_Button_CancelOrder')}</RedButton>
				</>
			),
			confirmAction: (confirm: boolean) => confirmFnc(confirm),
			content: (
				<div className="text-center">
					<h2>{t('BSPortal_Common_ConfirmationDialog_Sure')}</h2>
					<p>{t('Orders_OrderCard_CancelModal_Label_AboutCancel')}</p>
				</div>
			),
		});
	}

	private confirmOrder(shopOrderId: string, shopId?: string) {
		const { setStatus } = this.props;
		setStatus(shopOrderId, shopId, OrderStatus.Processing, '', false);
	}

	public componentDidMount() {}

	public componentWillUnmount() {
		this.props.clearData();
	}

	private handleShipOrderClick(id: string) {
		if (this.props.shipOrder) {
			this.props.shipOrder(id);
		}
	}
	private toggleShippingModal() {
		this.setState((s) => ({
			shippingModalOpen: !s.shippingModalOpen,
		}));
	}

	/**
	 * Split items by types and group by shop
	 * @param items
	 * @param getType
	 */
	private groupByShopAndSplitByType(
		shopOrders: IShopOrder[],
		getType?: (IOrderItem) => 'products' | 'services',
	): {
		/**
		 * key: shopId
		 */
		[key: string]: ProductsAndServices;
	} {
		// TODO: figure out how to differ services from products
		if (!getType) {
			getType = () => 'services';
		}

		const byTypes = {};
		shopOrders?.forEach((shopOrder) => {
			shopOrder?.items?.forEach((item) => {
				const itemType = getType(item);
				const shopItems = byTypes[item.shopId] || {};
				const shopProducts = shopItems[itemType] || [];

				shopProducts.push(item);

				shopItems[itemType] = shopProducts;
				byTypes[item.shopId] = shopItems;
			});
		});

		return byTypes;
	}

	private calculateAppointmentTimes = (shopOrderItems: IOrderItem[]) => {
		let firstTime = '';
		let lastTime = '';
		let totalDuration = null;

		if (!shopOrderItems?.length) {
			return { firstTime, lastTime, totalDuration };
		}

		shopOrderItems.forEach((item, idxI) => {
			item.bookedOrderItemDatas.forEach((boid, idxJ) => {
				if (idxI === 0 && idxJ === 0) {
					firstTime = boid.startTime;
					lastTime = boid.endTime;
				}

				if (moment(firstTime).isAfter(moment(boid?.startTime))) {
					firstTime = boid.startTime;
				}

				if (moment(lastTime).isBefore(moment(boid?.endTime))) {
					lastTime = boid.endTime;
				}
			});
		});

		totalDuration = moment(lastTime).diff(moment(firstTime), 'seconds') || null;

		return { firstTime, lastTime, totalDuration };
	};

	private renderShopOrderAppointments(
		order: IOrder,
		viewType: 'user' | 'shop',
		shopOrder: IShopOrder,
		shopProductsServices: ProductsAndServices,
		// _shippingData?: IShipping,
	) {
		const { t } = this.props;
		const firstItem = shopOrder.items[0];
		const isRecurringEvent = firstItem?.recurringBookingId ? true : false;

		// sort shopProductsServices services by schedule
		if (shopProductsServices?.services?.length) {
			shopProductsServices.services.sort((a, b) => moment(a.productSchedule).diff(moment(b.productSchedule)));
		}

		// sort shop order items by schedule
		if (shopOrder?.items?.length) {
			shopOrder.items.sort((a, b) => moment(a.productSchedule).diff(moment(b.productSchedule)));
		}

		// replace this with data
		// const promoCode: string = null;
		// const promoCodeDiscount: number = null;

		const { currencyKey } = shopOrder;

		const subtotal = shopOrder.total;

		const statusNumToStyleName = (status) => {
			if (status === undefined) return 'red';
			const statuses = ['gray', 'red', 'red', 'gray', 'gray', 'green'];
			if (statuses.length > status) {
				return statuses[status];
			} else {
				return 'red';
			}
		};
		const shopCode = firstItem.shopName
			.replace(/[^\w\s]/gi, '')
			.replace(/[^\x00-\x7F]+/, '')
			.replace(/[`~¨!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/gi, '')
			.replace(/\s/g, '');
		const { orderNo } = order;
		const timezone = firstItem.officeZoneInfo;
		const addedDate = this.isShopOrder() ? shopOrder?.addedDate : order?.addedDate;

		const cancelled = shopOrder.status == OrderStatus.Rejected || shopOrder.status == OrderStatus.Canceled;
		const booked = shopOrder.status === OrderStatus.Pending || shopOrder.status === OrderStatus.Processing;
		// Display as completed for now if the time is in the past as we don't update statuses correctly
		const completed =
			shopOrder.status === OrderStatus.Shipped ||
			shopOrder.status === OrderStatus.PartiallyShipped ||
			(shopOrder.status === OrderStatus.Processing &&
				shopOrder?.items?.[0]?.bookedOrderItemDatas?.[0]?.endTime &&
				momentWithTimezone(timezone, shopOrder.items[0].bookedOrderItemDatas[0].endTime).isBefore(new Date()));

		let orderStatusElement: JSX.Element = null;

		if (
			order?.paymentStatus === OrderPaymentStatus.Verified ||
			order?.message?.includes('Original paid order id') === true
		) {
			orderStatusElement = <span className="paid-online">{t('User_Order_PaidOnline')}</span>;
		} else if (order?.paymentStatus === OrderPaymentStatus.Rejected) {
			orderStatusElement = <span className="payment-rejected">{t('User_Order_PaymentRejected')}</span>;
		} else {
			orderStatusElement = <span className="payment-at-salon">{t('User_Order_PaymentAtSalon')}</span>;
		}

		const timeData = isRecurringEvent
			? this.calculateAppointmentTimes(shopOrder?.items?.[0] ? [shopOrder.items[0]] : [])
			: this.calculateAppointmentTimes(shopOrder?.items);

		const shopOrderContent = (
			<div className="shopOrderDetails__content" key={firstItem.shopId}>
				<div className="row">
					<div className="col-md-8 col-12">
						<div className="shopOrderDetails__time">
							<div className="shopOrderDetails__time__mainTime">
								{momentWithTimezone(timezone, timeData?.firstTime).format('LLLL')}
							</div>
							<div className="shopOrderDetails__time__subMainTime">
								{formatDuration(timeData?.totalDuration)} {t('BookAppointment_Schedule_Duration')}{' '}
								{momentWithTimezone(timezone, timeData?.lastTime).format('LT')}
							</div>
						</div>

						{shopProductsServices.services &&
							shopProductsServices.services.map((service, idx) => {
								// TODO: fix data
								const app: IAppointment = {
									shipping: {},
									shopOrder: shopOrder as IShopOrder,
									orderItem: service,
									order,
								};

								const recurringTimes = isRecurringEvent ? this.calculateAppointmentTimes([service]) : null;

								return (
									<React.Fragment key={service.productId}>
										{isRecurringEvent &&
										idx > 0 &&
										!moment(shopProductsServices?.services?.[idx - 1]?.productSchedule).isSame(
											moment(recurringTimes?.lastTime),
											'day',
										) ? (
											<>
												{/** Display new date above all of the items in that date */}
												<div className="shopOrderDetails__time newDate">
													<div className="shopOrderDetails__time__mainTime">
														{momentWithTimezone(timezone, recurringTimes?.firstTime).format('LLLL')}
													</div>
													<div className="shopOrderDetails__time__subMainTime">
														{formatDuration(recurringTimes.totalDuration)} {t('BookAppointment_Schedule_Duration')}{' '}
														{momentWithTimezone(timezone, recurringTimes?.lastTime).format('LT')}
													</div>
												</div>
											</>
										) : null}
										<Appointment
											appointment={app}
											onlyAppointmentInfo={true}
											isShopAppointment={true}
											type="past"
											key={idx}
											dontMarkCancelled={true}
										/>
									</React.Fragment>
								);
							})}
						{shopProductsServices.products && (
							<h3 className="separator-title">{t('Orders_OrderCard_Title_Products').toUpperCase()}</h3>
						)}
						{shopProductsServices.products && this.renderShopProductGroup(shopProductsServices.products, currencyKey)}
						<div className="shop-order-summary">
							{currencyKey !== undefined ? (
								<p className="total-list">
									<span>{t('BSPortal_Common_Total')}</span>
									<span>{formatCurrency(currencyKey, subtotal)}</span>
								</p>
							) : null}
						</div>
					</div>

					<div className="col-md-3 offset-md-1 col-12">
						{this.isShopOrder() ? (
							<div className="office-info">
								<AvatarSmall imageUrl={firstItem.shopPicture} title={firstItem.shopName} name={firstItem.shopName} />
								<div className="office-info__location">
									<p className="office-info__shopName">{firstItem.shopName}</p>
									<p className="office-info__officeLocation">{shopProductsServices.services[0].officeName}</p>
								</div>
							</div>
						) : null}

						<div className="payment-status">
							<p className="payment-status__upperText">{t('User_UserCard_PaymentStatus')}</p>
							{orderStatusElement}
						</div>

						<div className="order-status">
							<p className="order-status__upperText">
								{!this.isShopOrder() ? t('User_UserCard_AppointmentStatus') : t('User_UserCard_OrderStatus')}
							</p>
							{cancelled && <span className="cancelled-indicator">{t('BSPortal_Common_Cancelled')}</span>}
							{booked && !completed && <span className="booked-indicator">{t('BSPortal_Common_Booked')}</span>}
							{completed && <span className="completed-indicator">{t('BSPortal_Common_Completed')}</span>}
						</div>

						<div className="creation-date">
							<p className="creation-date__upperText">{t('Orders_OrderCard_CreationDate')}</p>
							<p>{parseDate(addedDate)}</p>
						</div>

						<div className="appointment-id">
							<p className="appointment-id__upperText">{t('Orders_OrderCard_AppointmentId')}</p>
							<p>{this.isShopOrder() ? shopOrder.id : `#${orderNo}` || `${order.id}`}</p>
						</div>

						<div className="order-buttons-row">
							<div className="order-buttons">
								{this.isShopOrder() && shopOrder.status == OrderStatus.Pending && (
									<EllipseBlackWhiteButton onClick={() => this.confirmOrder(shopOrder.id, shopOrder?.shopId)}>
										{t('Orders_OrderCard_Label_ApproveOrder')}
									</EllipseBlackWhiteButton>
								)}
								{[OrderStatus.Processing].includes(shopOrder.status) && this.isShopOrder() && (
									<EllipseGreyBlackButton
										className="cancel-button"
										onClick={() => this.rejectOrder(shopOrder.id, order.id, shopOrder?.shopId)}
									>
										{t('Orders_RejectOrder_Title')}
									</EllipseGreyBlackButton>
								)}
								{/** Hide cancel button for users after first appointment has passed */}
								{[OrderStatus.Processing].includes(shopOrder.status) &&
									!this.isShopOrder() &&
									shopOrder?.items?.[0]?.bookedOrderItemDatas?.[0]?.startTime &&
									momentWithTimezone(timezone, shopOrder.items[0].bookedOrderItemDatas[0].startTime).isAfter(
										new Date(),
									) && (
										<EllipseGreyBlackButton
											className="cancel-button"
											onClick={() => this.rejectOrder(shopOrder.id, order.id, shopOrder?.shopId)}
										>
											{t('Orders_UserOrderCard_Title_CancelAppointment')}
										</EllipseGreyBlackButton>
									)}
							</div>
							<div className={`status-indicator ${statusNumToStyleName(shopOrder.status)} d-none`}>
								{shopOrder.status !== undefined && t(`CodeList_OrderStatus_${shopOrder.status}`)}
							</div>
						</div>
					</div>
				</div>
			</div>
		);

		return (
			<React.Fragment key={shopOrder?.id}>
				{viewType == 'shop' ? (
					<>
						<div className="shopOrderDetails__shopInfo">
							<div className="shop-info">
								<AvatarSmall
									imageUrl={shopOrder?.customerData?.picture}
									title={shopOrder?.customerData?.name || order.shippingName}
									name={shopOrder?.customerData?.name || order.shippingName}
								/>
								<div className="shopOrderDetails__location">
									<p className="shopOrderDetails__shopName">{shopOrder?.customerData?.name || order.shippingName}</p>
									<p className="shopOrderDetails__officeLocation">
										{(shopOrder?.customerData?.callingCodeKey &&
											shopOrder?.customerData?.phoneNumber &&
											fullPhoneNumber(
												t,
												shopOrder?.customerData?.callingCodeKey,
												shopOrder?.customerData?.phoneNumber,
											)) ||
											fullPhoneNumber(t, order.shippingCallingCodeKey, order.shippingPhoneNumber)}
									</p>
								</div>
							</div>
						</div>
						{shopOrderContent}
					</>
				) : (
					<>
						<div className="shopOrderDetails__shopInfo">
							{firstItem ? (
								<Link className="shop-info" to={`/shop/${shopCode}`}>
									<AvatarSmall imageUrl={firstItem.shopPicture} title={firstItem.shopName} name={firstItem.shopName} />
									<div className="shopOrderDetails__location">
										<p className="shopOrderDetails__shopName">{firstItem.shopName}</p>
										<p className="shopOrderDetails__officeLocation">{shopProductsServices.services[0].officeName}</p>
									</div>
								</Link>
							) : null}
						</div>
						{shopOrderContent}
					</>
				)}
			</React.Fragment>
		);
	}

	// Function assumes products are from the same shop
	private renderShopProductGroup(products: IOrderItem[], currencyKey: number) {
		return (
			<div className="products">
				<div className="product-list">
					{products.map((product) => (
						<OrderProductItem
							productId={product.productId}
							productName={product.productName}
							productPicture={product.productPicture}
							productSKU={product.productSKU}
							productDescription={`MISSING DATA  •  MISSING DATA  •  ${this.props
								.t('BSPortal_Common_Qty')
								.toUpperCase()} ${product.qty}`}
							formattedPrice={formatCurrency(currencyKey, product.price)}
							key={`product-${product.id}`}
						/>
					))}
				</div>
			</div>
		);
	}

	private isShopOrder(_order?: IShopOrderCardData | IOrderCardData): _order is IShopOrderCardData {
		return this.props.viewType == 'shop';
	}

	public render() {
		if (this.props.orderCard.error) {
			return <ErrorPage {...this.props} message={this.props.orderCard.message} />;
		}

		const orderCard: IShopOrderCardData | IOrderCardData = this.props.orderCard;
		let order: IOrder;
		let shopOrders: IShopOrder[] = [];
		if (this.isShopOrder(orderCard)) {
			order = orderCard?.shopOrder?.order;
			shopOrders.push(orderCard?.shopOrder);
		} else {
			order = orderCard?.order;
			shopOrders = shopOrders.concat(orderCard?.order?.shopOrders);
		}

		const { t, backLink } = this.props;

		if (!order) {
			// Loading 'page'
			return (
				<div className="container shopOrderDetailsContainer">
					<LoadingOverlayWrapper
						active={this.props.orderCard.isFetching}
						spinner={true}
						text={t('BSPortal_Common_Loading')}
						background="rgba(0, 0, 0, 0.1)"
					></LoadingOverlayWrapper>
				</div>
			);
		}

		const grouped_products_by_shop = this.groupByShopAndSplitByType(shopOrders);
		const shippingById = {};
		const shopOrdersById = {};
		shopOrders.forEach((shopOrder) => {
			shopOrder?.shippings?.forEach((shippingData) => {
				shippingById[shippingData.id] = shippingData;
			});
			shopOrdersById[shopOrder.shopId] = shopOrder;
		});

		return (
			<PortalOneColumnLayout className="shopOrderDetailsContainer">
				<LoadingOverlayWrapper
					active={this.props.orderCard.isFetching}
					spinner={true}
					text={t('BSPortal_Common_Loading')}
					background="rgba(0, 0, 0, 0.1)"
				>
					<div className="row">
						<div className="col-12">
							<div className="shopOrderDetailsContainer__titleContainer">
								{this.props.viewType === 'user' ? (
									<>
										{renderIcon('arrow_left', 18, 18, 'shopOrderDetailsContainer__back', () => {
											browserHistory.push(backLink);
										})}
										<h1 className="shopOrderDetailsContainer__title">
											{t('Orders_UserOrderCard_Title_AppointmentDetails')}
										</h1>
									</>
								) : (
									<>
										{renderIcon('arrow_left', 18, 18, 'shopOrderDetailsContainer__back', () => {
											browserHistory.push(backLink);
										})}
										<h1 className="shopOrderDetailsContainer__title">
											{t('Orders_UserOrderCard_Title_AppointmentDetails')}
										</h1>
									</>
								)}
							</div>
						</div>
					</div>

					{shopOrders.map((shopOrder) =>
						grouped_products_by_shop[shopOrder.shopId]
							? this.renderShopOrderAppointments(
									order,
									this.props.viewType,
									shopOrder,
									grouped_products_by_shop[shopOrder.shopId],
							  )
							: null,
					)}
				</LoadingOverlayWrapper>
			</PortalOneColumnLayout>
		);
	}
}

export { OrderDetails };
